import React, { useMemo, useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth/auth-context';
import { useTeamApi } from '@/hooks/useTeamApi';
import { useVendorAccess } from '@/hooks/useVendorAccess';
import { 
  Permission, 
  createPermissionChecker,
  FEATURE_PERMISSIONS 
} from '@/lib/permissions/team-permissions';

/**
 * Hook to check user permissions in UI components
 * Provides granular permission checks for showing/hiding UI elements
 */
export function usePermissions() {
  const { user } = useAuth();
  const { data: teamMembers, isLoading: teamLoading } = useTeamApi.useTeamMembers({ enabled: !!user });
  const { isVendorOwner } = useVendorAccess();
  const [isOwnerCheck, setIsOwnerCheck] = useState<boolean | null>(null);
  const [isLoadingOwnerCheck, setIsLoadingOwnerCheck] = useState(true);

  // Check if user is vendor owner
  useEffect(() => {
    async function checkOwnership() {
      if (!user?.vendorId) {
        setIsOwnerCheck(false);
        setIsLoadingOwnerCheck(false);
        return;
      }

      try {
        // Check if user is vendor owner
        const ownerStatus = await isVendorOwner(user.vendorId);
        setIsOwnerCheck(ownerStatus);
      } catch (error) {
        console.error('Error checking ownership:', error);
        setIsOwnerCheck(false);
      } finally {
        setIsLoadingOwnerCheck(false);
      }
    }

    checkOwnership();
  }, [user?.vendorId, isVendorOwner]);

  const permissions = useMemo(() => {
    if (!user?.vendorId || teamLoading || isLoadingOwnerCheck) {
      return null;
    }

    // If user is vendor owner, give admin permissions regardless of team membership
    if (isOwnerCheck) {
      return createPermissionChecker('admin', {});
    }

    // Find current user's team member record
    const currentMember = teamMembers?.teamMembers?.find(member => member.user_id === user.id);
    
    if (!currentMember) {
      // User is NOT vendor owner AND not in team members = removed staff
      // Grant NO permissions at all - create explicit blocked permissions
      const blockedPermissions: Record<string, boolean> = {};
      Object.values(Permission).forEach(permission => {
        blockedPermissions[permission] = false;
      });
      return createPermissionChecker('staff', blockedPermissions);
    }

    // User is a legitimate team member
    return createPermissionChecker(
      currentMember.role,
      (currentMember.permissions as Record<string, boolean>) || {}
    );
  }, [user, teamMembers, teamLoading, isOwnerCheck, isLoadingOwnerCheck]);

  // Loading state
  if (teamLoading || isLoadingOwnerCheck || !permissions) {
    return {
      hasPermission: () => false,
      hasAnyPermission: () => false,
      hasAllPermissions: () => false,
      canViewFeature: () => false,
      canManageFeature: () => false,
      isLoading: true,
      // Convenience permission checks
      canViewDashboard: false,
      canViewCalendar: false,
      canViewOrders: false,
      canCreateOrders: false,
      canEditOrders: false,
      canDeleteOrders: false,
      canViewInventory: false,
      canManageInventory: false,
      canDeleteInventory: false,
      canBulkUpdateInventory: false,
      canViewEvents: false,
      canManageEvents: false,
      canViewPayments: false,
      canManagePayments: false,
      canViewTeam: false,
      canManageTeam: false,
      canInviteTeamMembers: false,
      canViewSettings: false,
      canManageSettings: false,
      canViewReports: false,
      canViewMessages: false,
      canManageMessages: false,
      canViewExpenses: false,
      canManageExpenses: false,
      canDeleteExpenses: false,
      canViewProfitLoss: false,
      canManageCostSettings: false,
    };
  }

  // Helper functions for common permission checks
  const hasPermission = (permission: Permission): boolean => {
    return permissions.hasPermission(permission);
  };

  const hasAnyPermission = (permissionList: Permission[]): boolean => {
    return permissions.hasAnyPermission(permissionList);
  };

  const hasAllPermissions = (permissionList: Permission[]): boolean => {
    return permissions.hasAllPermissions(permissionList);
  };

  const canViewFeature = (feature: keyof typeof FEATURE_PERMISSIONS): boolean => {
    return permissions.canViewFeature(feature);
  };

  const canManageFeature = (feature: keyof typeof FEATURE_PERMISSIONS): boolean => {
    return permissions.canManageFeature(feature);
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canViewFeature,
    canManageFeature,
    isLoading: false,

    // Convenience permission checks for common UI scenarios
    // Dashboard and Calendar
    canViewDashboard: hasPermission(Permission.VIEW_DASHBOARD),
    canViewCalendar: hasPermission(Permission.VIEW_CALENDAR),

    // Orders
    canViewOrders: hasPermission(Permission.VIEW_ORDERS),
    canCreateOrders: hasPermission(Permission.CREATE_ORDERS),
    canEditOrders: hasPermission(Permission.EDIT_ORDERS),
    canDeleteOrders: hasPermission(Permission.DELETE_ORDERS),
    canExportOrders: hasPermission(Permission.EXPORT_ORDERS),

    // Inventory
    canViewInventory: hasPermission(Permission.VIEW_INVENTORY),
    canManageInventory: hasPermission(Permission.MANAGE_INVENTORY),
    canDeleteInventory: hasPermission(Permission.DELETE_INVENTORY),
    canBulkUpdateInventory: hasPermission(Permission.BULK_UPDATE_INVENTORY),

    // Events
    canViewEvents: hasPermission(Permission.VIEW_EVENTS),
    canManageEvents: hasPermission(Permission.MANAGE_EVENTS),
    canCreateEvents: hasPermission(Permission.CREATE_EVENTS),
    canDeleteEvents: hasPermission(Permission.DELETE_EVENTS),

    // Payments
    canViewPayments: hasPermission(Permission.VIEW_PAYMENTS),
    canManagePayments: hasPermission(Permission.MANAGE_PAYMENTS),
    canProcessPayments: hasPermission(Permission.PROCESS_PAYMENTS),
    canViewPaymentHistory: hasPermission(Permission.VIEW_PAYMENT_HISTORY),

    // Products
    canViewProducts: hasPermission(Permission.VIEW_PRODUCTS),
    canManageProducts: hasPermission(Permission.MANAGE_PRODUCTS),
    canDeleteProducts: hasPermission(Permission.DELETE_PRODUCTS),

    // Team
    canViewTeam: hasPermission(Permission.VIEW_TEAM),
    canManageTeam: hasPermission(Permission.MANAGE_TEAM),
    canInviteTeamMembers: hasPermission(Permission.INVITE_TEAM_MEMBERS),
    canRemoveTeamMembers: hasPermission(Permission.REMOVE_TEAM_MEMBERS),

    // Settings
    canViewSettings: hasPermission(Permission.VIEW_SETTINGS),
    canManageSettings: hasPermission(Permission.MANAGE_VENDOR_SETTINGS),
    canManageBilling: hasPermission(Permission.MANAGE_BILLING),

    // Reports
    canViewReports: hasPermission(Permission.VIEW_REPORTS),
    canExportReports: hasPermission(Permission.EXPORT_REPORTS),

    // Messages
    canViewMessages: hasPermission(Permission.VIEW_MESSAGES),
    canManageMessages: hasPermission(Permission.MANAGE_MESSAGES),

    // Expenses and P&L
    canViewExpenses: hasPermission(Permission.VIEW_EXPENSES),
    canManageExpenses: hasPermission(Permission.MANAGE_EXPENSES),
    canDeleteExpenses: hasPermission(Permission.DELETE_EXPENSES),
    canViewProfitLoss: hasPermission(Permission.VIEW_PROFIT_LOSS),
    canManageCostSettings: hasPermission(Permission.MANAGE_COST_SETTINGS),
  };
}

/**
 * Component wrapper for permission-based rendering
 * Only renders children if user has the required permission
 */
interface RequirePermissionProps {
  permission: Permission;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function RequirePermission({ permission, children, fallback = null }: RequirePermissionProps) {
  const { hasPermission, isLoading } = usePermissions();

  if (isLoading) {
    return React.createElement(React.Fragment, null, fallback);
  }

  if (!hasPermission(permission)) {
    return React.createElement(React.Fragment, null, fallback);
  }

  return React.createElement(React.Fragment, null, children);
}

/**
 * Component wrapper for feature-based rendering
 * Only renders children if user can view the feature
 */
interface RequireFeatureAccessProps {
  feature: keyof typeof FEATURE_PERMISSIONS;
  action?: 'view' | 'manage';
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function RequireFeatureAccess({ 
  feature, 
  action = 'view', 
  children, 
  fallback = null 
}: RequireFeatureAccessProps) {
  const { canViewFeature, canManageFeature, isLoading } = usePermissions();

  if (isLoading) {
    return React.createElement(React.Fragment, null, fallback);
  }

  const hasAccess = action === 'manage' ? canManageFeature(feature) : canViewFeature(feature);

  if (!hasAccess) {
    return React.createElement(React.Fragment, null, fallback);
  }

  return React.createElement(React.Fragment, null, children);
}